import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, getQueryFn } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { useLocation } from 'wouter';

// Types
interface User {
  username: string;
  userType: 'admin' | 'client';
  accessoAbilitato?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password?: string) => Promise<void>;
  logout: () => Promise<void>;
  checkSession: () => void;
}

interface LoginRequest {
  username: string;
  password?: string;
}

interface AuthResponse {
  success: boolean;
  user?: User;
  message?: string;
}

interface SessionResponse {
  authenticated: boolean;
  user?: User;
  message?: string;
}

// Create the context
/**
 * AuthContext - Sistema autenticazione per CLIENTI (non admin)
 *
 * IMPORTANTE: Questo context gestisce SOLO l'auth per:
 * - /area-clienti
 * - /ricette (accesso premium)
 * - /assistenza
 *
 * Per le route ADMIN (/admin/*, /supabase-admin*) usare invece:
 * import { supabaseAuth } from '@/lib/supabase-auth';
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// AuthProvider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [location] = useLocation();

  // Skip Express auth check on Supabase admin routes (they use Supabase Auth)
  const isSupabaseAdminRoute = useMemo(() => {
    return location.startsWith('/admin') ||
      location.startsWith('/supabase-admin') ||
      location === '/dashboard-no';
  }, [location]);

  // Check current session (only for non-admin routes that use Express auth)
  const { data: sessionData, isLoading: sessionLoading, refetch: refetchSession } = useQuery<SessionResponse | null>({
    queryKey: ['/api/auth/me'],
    queryFn: getQueryFn({ on401: "returnNull" }),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !isSupabaseAdminRoute // Don't call /api/auth/me on admin routes
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async (loginData: LoginRequest): Promise<AuthResponse> => {
      const response = await apiRequest('POST', '/api/auth/login', loginData);
      return await response.json();
    },
    onSuccess: (data) => {
      if (data.success && data.user) {
        setUser(data.user);
        // Invalidate session query to refetch user data
        queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
        // Invalidate FAQ query to trigger refetch after login
        queryClient.invalidateQueries({ queryKey: ['faqs'] });
        // Invalidate purchases query to load user's purchases
        queryClient.invalidateQueries({ queryKey: ['myPurchases'] });
        toast({
          title: "Accesso effettuato",
          description: data.message || "Benvenuto nell'area riservata!"
        });
      }
    },
    onError: (error: any) => {
      const message = error?.message || "Errore durante l'accesso";
      toast({
        title: "Accesso negato",
        description: message,
        variant: "destructive"
      });
    }
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async (): Promise<AuthResponse> => {
      const response = await apiRequest('POST', '/api/auth/logout');
      return await response.json();
    },
    onSuccess: () => {
      setUser(null);
      // Clear all query cache to ensure clean state
      queryClient.clear();
      toast({
        title: "Logout effettuato",
        description: "Sei stato disconnesso dall'area riservata."
      });
    },
    onError: (error: any) => {
      // Even if logout fails on server, clear local state
      setUser(null);
      queryClient.clear();
      const message = error?.message || "Errore durante il logout";
      toast({
        title: "Logout",
        description: message,
        variant: "destructive"
      });
    }
  });

  // Update user state when session data changes
  useEffect(() => {
    if (sessionData) {
      const typedData = sessionData as SessionResponse;
      if (typedData.authenticated && typedData.user) {
        setUser(typedData.user);
      } else {
        setUser(null);
      }
    } else {
      setUser(null);
    }
  }, [sessionData]);

  // Auth functions
  const login = async (username: string, password?: string) => {
    await loginMutation.mutateAsync({ username, password });
  };

  const logout = async () => {
    await logoutMutation.mutateAsync();
  };

  const checkSession = () => {
    refetchSession();
  };

  const isAuthenticated = !!user;
  const isLoading = sessionLoading || loginMutation.isPending || logoutMutation.isPending;

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};